/**
 * Client-side navigation helper for the SPA.
 *
 * Why this exists: a plain `<a href="/...">` to an in-app route causes a
 * full page reload. The DEK lives only in memory (see session.svelte.ts)
 * and gets dropped on reload, which means clicking an internal link as a
 * regular browser navigation makes private content un-decryptable. We
 * route in-app navigation through history.pushState instead so the SPA
 * keeps its in-memory state (DEK, decrypted activities, scroll position).
 *
 * Links still keep their `href` attribute so:
 *   - Right-click → "Open in new tab" works
 *   - Cmd/Ctrl + click opens in a new tab
 *   - Middle-click opens in a new tab
 *   - Copy-link-address works
 *   - Screen readers announce them as links
 * onSpaLink only swallows the *plain* left-click.
 */

export function navigate(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState({}, '', path);
  // App.svelte's popstate listener owns the "parse + apply route" logic;
  // dispatch one ourselves so pushState behaves the same as back/forward.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function onSpaLink(e: MouseEvent, path: string): void {
  // Let the browser handle modified clicks (new tab, new window, download)
  // and right/middle clicks. We only take over the plain left-click.
  if (e.defaultPrevented) return;
  if (e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  navigate(path);
}

/**
 * "Back" navigation for in-app back buttons. Uses real browser history
 * when there's prior history to return to (preserves the user's actual
 * path through the SPA). Falls back to navigate(fallback) on cold-loads
 * — when the user opened the URL directly in a new tab and there's no
 * prior entry to back into.
 *
 * `window.history.length` is browser-dependent but length === 1 is a
 * reliable cold-load signal: every browser counts the current entry.
 */
export function goBack(fallback: string): void {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    navigate(fallback);
  }
}
